import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatError, WASTE_TYPES, wasteLabel, wasteColor } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import { PlusCircle, Scale, Recycle, Clock, Ban, Award } from "lucide-react";
import { toast } from "sonner";

const fmtKg = (v) => `${(v || 0).toFixed(2)} kg`;

export default function CitizenDashboard() {
  const [balance, setBalance] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [b, s] = await Promise.all([api.get("/balance/me"), api.get("/submissions")]);
        setBalance(b.data);
        setSubs(s.data);
      } catch (e) { toast.error(formatError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <Layout
      title="I tuoi conferimenti"
      subtitle="Visualizza i tuoi conferimenti approvati, in attesa o contestati e il saldo accumulato ai fini dello sconto TARI."
      actions={
        <Link to="/submissions/new">
          <Button data-testid="new-submission-btn" className="rounded-full gap-2 h-11 px-5">
            <PlusCircle className="w-4 h-4" /> Nuovo conferimento
          </Button>
        </Link>
      }
    >
      {/* Stat grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Scale} label="Totale approvato" value={fmtKg(balance?.total_kg)} tone="primary" testid="stat-total" />
        <StatCard icon={Award} label="Riconosciuto TARI" value={fmtKg(balance?.recognized_kg)} tone="success" testid="stat-recognized" />
        <StatCard icon={Clock} label="In attesa" value={balance?.pending_count ?? 0} tone="warning" testid="stat-pending" />
        <StatCard icon={Ban} label="Contestati" value={balance?.rejected_count ?? 0} tone="danger" testid="stat-rejected" />
      </div>

      {/* By type */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold">Ripartizione per tipologia</h2>
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Solo approvati</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {WASTE_TYPES.map((w) => {
              const b = balance?.by_type?.[w.value];
              return (
                <div key={w.value} className="p-4 rounded-lg border border-gray-200 bg-white">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: w.color }} />
                    <span className="text-xs font-medium text-gray-600">{w.label}</span>
                  </div>
                  <div className="mt-2 font-display text-2xl font-semibold">{fmtKg(b?.total_kg)}</div>
                  <div className="text-xs text-muted-foreground">{b?.count || 0} conferimenti</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Submissions table */}
      <Card>
        <CardContent className="p-0">
          <div className="p-6 pb-2">
            <h2 className="font-display text-lg font-semibold">Storico conferimenti</h2>
          </div>
          {loading ? (
            <div className="p-8 text-sm text-muted-foreground">Caricamento…</div>
          ) : subs.length === 0 ? (
            <EmptyState />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Codice</TableHead>
                  <TableHead>Tipologia</TableHead>
                  <TableHead>Peso verificato</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subs.map((s) => (
                  <TableRow key={s.id} data-testid={`sub-row-${s.id}`} className="hover:bg-gray-50">
                    <TableCell className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString("it-IT")}</TableCell>
                    <TableCell className="font-mono text-xs">{s.barcode_full || <span className="text-amber-600 font-sans">in verifica</span>}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: wasteColor(s.waste_type) }} />
                        {wasteLabel(s.waste_type)}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">{s.verified_weight_kg != null ? fmtKg(s.verified_weight_kg) : "—"}</TableCell>
                    <TableCell><StatusBadge status={s.status} /></TableCell>
                    <TableCell className="text-right pr-6">
                      <Link to={`/submissions/${s.id}`} className="text-primary text-sm font-medium hover:underline" data-testid={`view-sub-${s.id}`}>
                        Dettagli →
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}

function StatCard({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    primary: "bg-primary/10 text-primary",
    success: "bg-emerald-100 text-emerald-700",
    warning: "bg-amber-100 text-amber-700",
    danger: "bg-red-100 text-red-700",
  };
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="w-5 h-5" /></div>
        <div className="mt-4 text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="mt-1 font-display text-3xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}

function EmptyState() {
  return (
    <div className="p-10 text-center">
      <Recycle className="w-10 h-10 mx-auto text-gray-300 mb-3" />
      <div className="font-display text-lg font-semibold">Nessun conferimento ancora</div>
      <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
        Recati presso un punto di raccolta, pesa il materiale, scatta la foto e caricala qui per dare inizio al tracciamento.
      </p>
      <Link to="/submissions/new"><Button className="mt-5 rounded-full">Nuovo conferimento</Button></Link>
    </div>
  );
}
